import { z } from "zod";
import type { Faq } from "../data/faq";
import {
  internetConnectionTypes,
  type internetDataPolicies,
  type internetInstallationMethods,
  type InternetAdditionalService,
  type InternetEquipment,
  type InternetFactNotes,
} from "../data/internet";

export type InternetConnectionType = (typeof internetConnectionTypes)[number];
export type InternetInstallationMethod =
  (typeof internetInstallationMethods)[number];
export type InternetDataPolicy = (typeof internetDataPolicies)[number];
export type InternetEquipmentOwnership = NonNullable<
  InternetEquipment["ownership"]
>;
export type InternetExtraType = InternetAdditionalService["type"];

/** Display order for connection types: fixed lines first, then wireless. */
export const internetConnectionTypeOrder: readonly InternetConnectionType[] = [
  "FTTH",
  "CABLE",
  "DSL",
  "FIXED_5G",
  "FIXED_4G",
  "SATELLITE",
];

/**
 * One published plan in the compact shape every interactive surface shares
 * (search, compare, finder, provider catalogue). Brand and plan names are not
 * translated, so the payload is identical for every locale.
 */
export type InternetPlan = {
  id: string;
  name: string;
  familyKey: string | null;
  provider: { slug: string; name: string };
  network: { slug: string; name: string } | null;
  connectionType: InternetConnectionType;
  downloadMbps: number | null;
  uploadMbps: number | null;
  minimumContractMonths: number | null;
  regularMonthlyPriceCents: number | null;
  introMonthlyPriceCents: number | null;
  introDurationMonths: number | null;
  activationFeeCents: number | null;
  installationFeeCents: number | null;
  equipmentUpfrontFeeCents: number | null;
  equipmentMonthlyFeeCents: number | null;
  networkMonthlyFeeCents: number | null;
  /**
   * True when the network bills its own fee in several tiers (for example
   * cooperative membership categories); `networkMonthlyFeeCents` then holds
   * the lowest tier, so prices read as "from".
   */
  networkFeeVaries: boolean;
  installationMethod: InternetInstallationMethod;
  equipmentOwnership: InternetEquipmentOwnership | null;
  wifiStandard: string | null;
  ipv6: boolean | null;
  publicIpv4: boolean | null;
  staticIp: boolean | null;
  ownRouterSupported: boolean | null;
  extras: InternetExtraType[];
  dataPolicy: InternetDataPolicy;
  officialUrl: string;
  postcodeCheckUrl: string;
  lastVerifiedAt: string | null;
};

export type InternetPlanSource = {
  id: string;
  url: string;
  checkedAt: string;
};

/** Everything the plan drawer renders, served by the public plan endpoint. */
export type InternetPlanDetailData = InternetPlan & {
  providerWebsiteUrl: string;
  additionalServices: InternetAdditionalService[];
  sources: InternetPlanSource[];
  text: {
    description: string;
    pricingDetails: string | null;
    contractDetails: string | null;
    installationDetails: string | null;
    equipmentDetails: string | null;
    availabilityDetails: string | null;
    movingDetails: string | null;
    additionalServicesDetails: string | null;
    considerations: string[];
    factNotes: InternetFactNotes;
    faqs: Faq[];
  };
};

export const INTERNET_COMPARE_LIMIT = 4;

/** Plan ids are lowercase slugs that may contain underscores. */
export const INTERNET_PLAN_ID_PATTERN = /^[a-z0-9_-]{3,120}$/;

export const internetPlanSorts = ["PRICE", "SPEED", "VALUE", "A-Z"] as const;
export type InternetPlanSort = (typeof internetPlanSorts)[number];
export const INTERNET_DEFAULT_SORT: InternetPlanSort = "PRICE";

export const internetSpeedFilters = ["ANY", "100", "500", "1000", "2000"] as const;
export type InternetSpeedFilter = (typeof internetSpeedFilters)[number];

export const internetTermFilters = ["ANY", "MONTHLY", "YEAR"] as const;
export type InternetTermFilter = (typeof internetTermFilters)[number];

export const internetPriceFilters = ["ANY", "35", "45", "55", "65"] as const;
export type InternetPriceFilter = (typeof internetPriceFilters)[number];

export const INTERNET_PLAN_PAGE_SIZE = 18;

const flag = z
  .union([z.literal("1"), z.literal("true")])
  .transform(() => true)
  .optional()
  .catch(undefined);

/**
 * Plan-search filters. The page shell never reads these on the server; the
 * client parses them from the URL and filters the shared catalogue payload.
 */
export const internetPlanSearchParams = z.object({
  search: z.string().optional().catch(undefined),
  type: z
    .enum(["ALL", ...internetConnectionTypes])
    .optional()
    .default("ALL")
    .catch("ALL"),
  speed: z
    .enum(internetSpeedFilters)
    .optional()
    .default("ANY")
    .catch("ANY"),
  term: z.enum(internetTermFilters).optional().default("ANY").catch("ANY"),
  price: z
    .enum(internetPriceFilters)
    .optional()
    .default("ANY")
    .catch("ANY"),
  provider: z.string().optional().catch(undefined),
  network: z.string().optional().catch(undefined),
  symmetric: flag,
  router: flag,
  ip: flag,
  sort: z
    .enum(internetPlanSorts)
    .optional()
    .default(INTERNET_DEFAULT_SORT)
    .catch(INTERNET_DEFAULT_SORT),
  page: z.coerce.number().int().min(1).optional().default(1).catch(1),
});

export type InternetPlanSearchParams = z.infer<typeof internetPlanSearchParams>;

export const parseInternetPlanSearchParams = (
  params: unknown,
): InternetPlanSearchParams => {
  const parsed = internetPlanSearchParams.safeParse(params ?? {});
  return parsed.success ? parsed.data : internetPlanSearchParams.parse({});
};

export const getInternetPlanSearchUrlQuery = (
  data: InternetPlanSearchParams,
) => {
  const params = new URLSearchParams();
  if (data.search?.trim()) params.set("search", data.search.trim());
  if (data.type !== "ALL") params.set("type", data.type);
  if (data.speed !== "ANY") params.set("speed", data.speed);
  if (data.term !== "ANY") params.set("term", data.term);
  if (data.price !== "ANY") params.set("price", data.price);
  if (data.provider) params.set("provider", data.provider);
  if (data.network) params.set("network", data.network);
  if (data.symmetric) params.set("symmetric", "1");
  if (data.router) params.set("router", "1");
  if (data.ip) params.set("ip", "1");
  if (data.sort !== INTERNET_DEFAULT_SORT) params.set("sort", data.sort);
  if (data.page > 1) params.set("page", String(data.page));
  return params;
};

/** Parses a `plans` query value into a de-duplicated, capped list of plan ids. */
export const parseInternetComparePlanIds = (value: string | null) =>
  Array.from(
    new Set(
      (value ?? "")
        .split(",")
        .map((id) => id.trim())
        .filter((id) => INTERNET_PLAN_ID_PATTERN.test(id)),
    ),
  ).slice(0, INTERNET_COMPARE_LIMIT);
